import {Alert, Box, MenuItem, TextField, Typography} from '@mui/material';
import React, {useEffect} from 'react';
import ExpandableText from 'src/components/ExpandableText';
import {ANALYSIS_DESCRIPTION_MAX_CHARS, ANALYSIS_NAME_MAX_CHARS} from '../../constants';

const availabilityGridColumns = 'repeat(3, 1fr)';
const AVAILABILITY_TEXT_PREVIEW_MAX_CHARS = 40;

interface MotifAnalysisStep0Props {
    formData: MotifFormData;
    setFormData: React.Dispatch<React.SetStateAction<MotifFormData>>;
    handleNext: (isValid: boolean) => void;
    error: string;
    differentialBindingAnalysisList: DbaMotifAnalysisOption[];
    isLoading: boolean;
}

/**
 * Allows user to choose name, description, and dba for new motif analysis
 * TODO: large amount of overlap with ChIPAnalysisDownStreamStep0
 */
function MotifAnalysisStep0({
    formData,
    setFormData,
    handleNext,
    error,
    differentialBindingAnalysisList,
    isLoading
}: MotifAnalysisStep0Props) {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value} = e.target;
        setFormData(previousFormData => ({...previousFormData, [name]: value}));
    };

    const selectedDba = differentialBindingAnalysisList.find(
        analysis => analysis.analysis_id === formData.differential_binding_analysis_id
    );
    const visibleInputs = selectedDba?.inputs ?? [];
    const availableInputCount = visibleInputs.filter(input => input.motif_available).length;
    const isValid = formData.name.trim().length > 2 && availableInputCount > 0 && !error;

    useEffect(() => {
        handleNext(isValid);
    }, [handleNext, isValid]);

    return (
        <Box
            p={3}
            my={1}
            sx={{bgcolor: 'surface-container-lowest', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: 3}}>
            <TextField
                name="name"
                label="Analysis Name"
                fullWidth
                value={formData.name}
                placeholder="Enter a name"
                onChange={handleInputChange}
                inputProps={{maxLength: ANALYSIS_NAME_MAX_CHARS}}
            />

            <TextField
                name="description"
                label="Description (optional)"
                fullWidth
                value={formData.description}
                placeholder="Enter a description"
                onChange={handleInputChange}
                inputProps={{maxLength: ANALYSIS_DESCRIPTION_MAX_CHARS}}
            />

            <TextField
                select
                name="differential_binding_analysis_id"
                label="Differential Binding Analysis"
                fullWidth
                value={formData.differential_binding_analysis_id}
                onChange={handleInputChange}
                disabled={isLoading || !!error || differentialBindingAnalysisList.length === 0}
                helperText={
                    error
                        ? 'Unable to load differential binding analyses.'
                        : isLoading
                          ? 'Loading analyses...'
                          : differentialBindingAnalysisList.length === 0
                            ? 'No completed differential binding analyses found. A completed differential binding analysis is required to create a motif analysis.'
                            : 'Selecting a differential binding analysis will import its data into your new analysis.'
                }>
                {differentialBindingAnalysisList.map(option => (
                    <MenuItem key={option.analysis_id} value={option.analysis_id}>
                        {option.analysis_name}
                    </MenuItem>
                ))}
            </TextField>

            {selectedDba && (
                <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="title" size="medium">
                        Differential binding regions
                    </Typography>
                    <Typography variant="body" size="small" color="text.secondary">
                        Motif analysis uses differentially bound regions from the selected analysis to identify enriched motifs. At least
                        one enrichment direction must have regions available to continue.
                    </Typography>
                </Box>
            )}

            {visibleInputs.length > 0 && (
                <Box role="table" width="100%" alignSelf="stretch" overflow="hidden" border={1} borderColor="divider" borderRadius={1}>
                    <Box
                        role="row"
                        display="grid"
                        gridTemplateColumns={availabilityGridColumns}
                        columnGap={2}
                        px={2}
                        py={1.5}
                        width="100%"
                        boxSizing="border-box"
                        color="text.secondary"
                        bgcolor="surface-container-low">
                        {['Comparison', 'Enriched in', 'Regions for motif analysis'].map(column => (
                            <Typography key={column} role="columnheader" variant="title" size="medium">
                                {column}
                            </Typography>
                        ))}
                    </Box>
                    {visibleInputs.map(input => (
                        <Box
                            key={input.bed_filename}
                            role="row"
                            display="grid"
                            gridTemplateColumns={availabilityGridColumns}
                            columnGap={2}
                            px={2}
                            py={1.5}
                            width="100%"
                            boxSizing="border-box"
                            borderTop={1}
                            borderColor="divider"
                            sx={{opacity: input.motif_available ? 1 : 0.55}}>
                            <Box role="cell" minWidth={0}>
                                <ExpandableText value={input.comparison} maxChars={AVAILABILITY_TEXT_PREVIEW_MAX_CHARS} />
                            </Box>
                            <Box role="cell" minWidth={0}>
                                <ExpandableText value={input.enrichment_direction} maxChars={AVAILABILITY_TEXT_PREVIEW_MAX_CHARS} />
                            </Box>
                            <Typography role="cell" variant="body" size="large">
                                {input.motif_available ? 'Regions available' : 'No regions available'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {selectedDba && visibleInputs.length === 0 && (
                <Alert severity="warning">
                    Differential binding region availability is unavailable for this analysis. Select another differential binding analysis,
                    or rerun this analysis to regenerate availability information. Motif analysis cannot continue with this selection.
                </Alert>
            )}

            {selectedDba && visibleInputs.length > 0 && availableInputCount === 0 && (
                <Alert severity="warning">
                    This analysis has no differentially bound regions available for motif analysis. Select another differential binding
                    analysis, or adjust the comparison or parameters and rerun it.
                </Alert>
            )}

            {selectedDba && availableInputCount > 0 && availableInputCount < visibleInputs.length && (
                <Alert severity="warning">
                    Only {availableInputCount} of {visibleInputs.length} enrichment directions {availableInputCount === 1 ? 'has' : 'have'}{' '}
                    differentially bound regions. Directions with no regions will not be included in the motif analysis.
                </Alert>
            )}
        </Box>
    );
}

export default MotifAnalysisStep0;
